import { IColumnHeaderParams, IPage, IPageRange } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import { Reducer } from 'redux';
import ContentConstants from '../contentConstants';
import { DistributionContactsListSourceType, DistributionType, IAttributeVm, IColumn, IDistributionContact } from '../models';
import { SurveyCompletionStatusType } from '../models/SurveyCompletionStatusType';
import { SurveyStatusType } from '../models/SurveyStatusType';
import * as AttributeAPI from '../services/AttributeAPI';
import * as DistributionContactAPI from '../services/DistributionContactAPI';
import { ColumnHelper, FileDownloadHelper } from '../utilities';
import { DistributionHelper } from '../utilities/DistributionHelper';
import { IApplicationState } from './';
import {
	IDistibutionContactSetDuplicateEmailSwitchState,
	IDistibutionContactToggleDuplicateEmailSwitch,
	IDistributionContactAddSelected,
	IDistributionContactClearSelected,
	IDistributionContactDeletedRequested,
	IDistributionContactDeleteFailed,
	IDistributionContactDeleteSucceeded,
	IDistributionContactMultipleDeleteFailed,
	IDistributionContactMultipleDeleteRequested,
	IDistributionContactMultipleDeleteSucceeded,
	IDistributionContactRemoveSelected,
	IDistributionContactsColumnsGetFailed,
	IDistributionContactsColumnsGetRequested,
	IDistributionContactsColumnsGetSucceeded,
	IDistributionContactsCountGetFailed,
	IDistributionContactsCountGetRequested,
	IDistributionContactsCountGetSucceeded,
	IDistributionContactsGetFailed,
	IDistributionContactsGetRequested,
	IDistributionContactsGetSucceeded,
	IDistributionContactsModalClose,
	IDistributionContactsModalOpen,
	IDistributionContactsResetDefaultColumns,
	IDistributionContactToggleDeleteWarningModal,
	IDistributionContactUpdateStatusAfterReopen,
	IDistributionContactUpdateStatusAfterReset,
	IResetResendReopenDefaultColumns,
} from './Actions';
import { ActionTypes } from './ActionTypes';

export enum DistributionColumns {
	firstName = 'firstName',
	lastName = 'lastName',
	employeeId = 'employeeId',
	emailAddress = 'emailAddress',
	surveyCompletionStatus = 'surveyCompletionStatus',
	accessCode = 'accessCode',
	actions = 'actions',
}

export const defaultPageParams: IPageRange = { offset: 0, limit: 25 };
export const defaultSortParams: IColumnHeaderParams = {
	columns: [],
	sortedBy: DistributionColumns.firstName,
	sortAsc: true,
};

export const firstNameColumn: IColumn = {
	name: DistributionColumns.firstName,
	displayText: 'First Name',
	displayOrder: 0,
	disabled: true,
	checked: true,
	isSortable: true,
	sortAscByDefault: true,
};
export const lastNameColumn: IColumn = {
	name: DistributionColumns.lastName,
	displayText: 'Last Name',
	displayOrder: 1,
	disabled: true,
	checked: true,
	isSortable: true,
	sortAscByDefault: false,
};
export const employeeIdColumn: IColumn = {
	name: DistributionColumns.employeeId,
	displayText: 'Employee ID',
	displayOrder: 2,
	disabled: false,
	checked: false,
};
export const emailAddressColumn: IColumn = {
	name: DistributionColumns.emailAddress,
	displayText: 'Email Address',
	displayOrder: 3,
	disabled: false,
	checked: true,
	isSortable: true,
	sortAscByDefault: false,
};
export const surveyStatusColumn: IColumn = {
	name: DistributionColumns.surveyCompletionStatus,
	displayText: 'Survey Status',
	displayOrder: 4,
	disabled: false,
	checked: true,
	isSortable: true,
	sortAscByDefault: false,
};
export const accessCodeColumn: IColumn = {
	name: DistributionColumns.accessCode,
	displayText: 'Access Code',
	displayOrder: 4,
	disabled: false,
	checked: false,
	isSortable: true,
	sortAscByDefault: false,
};
export const actionsColumn: IColumn = {
	name: DistributionColumns.actions,
	displayText: 'Actions',
	displayOrder: 5,
	disabled: true,
	checked: true,
};

export const distributionContactDefaultColumns: IColumn[] = [
	{ ...firstNameColumn },
	{ ...lastNameColumn },
	{ ...employeeIdColumn },
	{ ...emailAddressColumn },
	{ ...accessCodeColumn },
];
export const surveyParticipantDefaultColumns: IColumn[] = [
	{ ...firstNameColumn },
	{ ...lastNameColumn },
	{ ...employeeIdColumn },
	{ ...emailAddressColumn },
	{ ...accessCodeColumn },
	{ ...surveyStatusColumn },
];

export interface IDistributionContactState {
	columns: IColumn[];
	distributionContacts: IDistributionContact[];
	distributionType?: DistributionType;
	isDeleteDistributionContactsModalOpen?: boolean;
	isDeletingDistributionContacts?: boolean;
	loadingContacts: boolean;
	pageRange: IPageRange;
	searchText: string;
	selectedDistributionContacts: IDistributionContact[];
	sortParams: IColumnHeaderParams;
	displayDuplicateEmailAddresses: boolean;
	contactsCount: number;
}

export const defaultState: IDistributionContactState = {
	columns: [...distributionContactDefaultColumns, actionsColumn],
	distributionContacts: [],
	isDeleteDistributionContactsModalOpen: false,
	isDeletingDistributionContacts: false,
	loadingContacts: false,
	pageRange: defaultPageParams,
	searchText: '',
	selectedDistributionContacts: [],
	sortParams: defaultSortParams,
	displayDuplicateEmailAddresses: false,
	contactsCount: 0,
};

type KnownActions =
	| IDistributionContactsGetRequested
	| IDistributionContactsGetFailed
	| IDistributionContactsGetSucceeded
	| IDistributionContactsCountGetRequested
	| IDistributionContactsCountGetFailed
	| IDistributionContactsCountGetSucceeded
	| IDistributionContactsModalOpen
	| IDistributionContactsModalClose
	| IDistributionContactDeletedRequested
	| IDistributionContactDeleteFailed
	| IDistributionContactDeleteSucceeded
	| IDistributionContactMultipleDeleteRequested
	| IDistributionContactMultipleDeleteFailed
	| IDistributionContactMultipleDeleteSucceeded
	| IDistributionContactsColumnsGetFailed
	| IDistributionContactsColumnsGetRequested
	| IDistributionContactsColumnsGetSucceeded
	| IDistributionContactAddSelected
	| IDistributionContactRemoveSelected
	| IDistributionContactToggleDeleteWarningModal
	| IDistributionContactsResetDefaultColumns
	| IDistributionContactUpdateStatusAfterReopen
	| IDistributionContactUpdateStatusAfterReset
	| IDistributionContactClearSelected
	| IDistibutionContactToggleDuplicateEmailSwitch
	| IDistibutionContactSetDuplicateEmailSwitchState
	| IResetResendReopenDefaultColumns;

export function GetSurveyParticipantColumns(
	distributionContactsListSourceType: DistributionContactsListSourceType,
	distributionType: DistributionType,
) {
	let columns =
		distributionContactsListSourceType === DistributionContactsListSourceType.ResetResendReopen
			? [...surveyParticipantDefaultColumns]
			: [...distributionContactDefaultColumns];

	if (DistributionHelper.isAnonymous(distributionType)) {
		columns = _.filter(columns, (column) => {
			return column.name !== DistributionColumns.surveyCompletionStatus;
		});
	}
	return columns;
}

export const ActionCreators = {
	GetDistributionContacts:
		(
			surveyId: number,
			audienceId: number,
			pageRange: IPageRange,
			searchText: string,
			sortedBy: string,
			sortAsc: boolean,
			selectedColumns?: IColumn[],
		) =>
		(dispatch: any, getState: () => IApplicationState) => {
			const columns = _.cloneDeep(selectedColumns);
			if (_.some(columns, (c) => c.name === DistributionColumns.surveyCompletionStatus)) {
				_.remove(columns as IColumn[], (c) => c.name === DistributionColumns.surveyCompletionStatus);
			}

			dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_GET_REQUESTED });
			const extraColumns = ColumnHelper.GetExtraColumnNames([...distributionContactDefaultColumns, actionsColumn], columns);

			return DistributionContactAPI.getDistributionContacts(
				surveyId,
				audienceId,
				pageRange.offset,
				pageRange.limit,
				searchText,
				sortedBy,
				sortAsc,
				extraColumns,
			)
				.then((pagedDistributionContacts: IPage<IDistributionContact>) => {
					dispatch({
						type: ActionTypes.DISTRIBUTION_CONTACTS_GET_SUCCEEDED,
						payload: { pagedDistributionContacts, searchText, sortedBy, sortAsc, columns: columns },
					});
				})
				.catch((ex: any) => {
					dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_GET_FAILED });
				});
		},

	OpenDistributionContactModal: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_MODAL_OPEN });
	},

	CloseDistributionContactModal: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_MODAL_CLOSE });
	},

	GetColumns:
		(
			distributionContactsListSourceType: DistributionContactsListSourceType,
			audienceId: number,
			surveyStatus: SurveyStatusType,
			distributionType: DistributionType,
		) =>
		(dispatch: any) => {
			if (DistributionHelper.isNotAnonymous(distributionType)) {
				dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_COLUMNS_GET_REQUESTED });

				return AttributeAPI.getConfiguredClientAttributesByAudience(audienceId)
					.then((attributes: IAttributeVm[]) => {
						dispatch({
							type: ActionTypes.DISTRIBUTION_CONTACTS_COLUMNS_GET_SUCCEEDED,
							payload: { distributionContactsListSourceType, attributes, distributionType, surveyStatus },
						});
					})
					.catch((ex: any) => {
						dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_COLUMNS_GET_FAILED });
					});
			} else {
				if (distributionContactsListSourceType === DistributionContactsListSourceType.ResetResendReopen) {
					dispatch({ type: ActionTypes.RESET_RESEND_REOPEN_DEFAULT_COLUMNS });
				} else {
					dispatch({ type: ActionTypes.DISTRIBUTION_CONTACTS_RESET_DEFAULT_COLUMNS });
				}
			}
		},
	ToggleCheckboxes: (checked: boolean, distributionContacts: IDistributionContact[]) => (dispatch: any) => {
		if (checked) {
			dispatch({
				type: ActionTypes.DISTRIBUTION_CONTACT_ADD_SELECTED,
				payload: {
					distributionContacts,
				},
			});
		} else {
			dispatch({
				type: ActionTypes.DISTRIBUTION_CONTACT_REMOVE_SELECTED,
				payload: {
					distributionContacts,
				},
			});
		}
	},
	DeleteDistributionContacts:
		(surveyId: number, audienceId: number, distributionContactIds: number[]) => (dispatch: any, getState: () => IApplicationState) => {
			dispatch({ type: ActionTypes.DISTRIBUTION_CONTACT_MULTIPLE_DELETE_REQUESTED });

			return DistributionContactAPI.deleteDistributionContacts(surveyId, audienceId, distributionContactIds)
				.then(() => {
					dispatch({ type: ActionTypes.DISTRIBUTION_CONTACT_MULTIPLE_DELETE_SUCCEEDED });
					dispatch({
						type: ActionTypes.TOAST_INFO_NOTIFICATION,
						payload: ContentConstants.TOAST_DISTRIBUTION_CONTACT_DELETED.replace('{total}', distributionContactIds.length.toString()),
					});

					const { pageRange, searchText, sortParams, columns } = getState().distributionContacts;

					// get contacts
					dispatch(
						ActionCreators.GetDistributionContacts(
							surveyId,
							audienceId,
							pageRange,
							searchText,
							sortParams.sortedBy!,
							sortParams.sortAsc!,
							columns,
						),
					);
				})
				.catch((ex: any) => {
					dispatch({ type: ActionTypes.DISTRIBUTION_CONTACT_MULTIPLE_DELETE_FAILED });
				});
		},

	ToggleDeleteDistributionContactsWarningModal: (isOpen: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_CONTACT_TOGGLE_DELETE_WARNING_MODAL, payload: isOpen });
	},

	ClearSelectedDistributionContacts: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.DISTRIBUTION_CONTACT_CLEAR_SELECTED });
	},
	SetDisplayDuplicateEmailsState: (isSwitchOn = false) => ({
		type: ActionTypes.DISTRIBUTION_CONTACTS_SET_DISPLAY_DUPLICATE_EMAIL_STATE,
		payload: isSwitchOn,
	}),
	ExportDistributionContactList: (surveyId: number, audienceId: number, selectedContacts: IDistributionContact[]) => (dispatch: any) => {
		const selectedContactIds = selectedContacts.map((sc) => sc.distributionContactId);
		return DistributionContactAPI.downloadDistributionContactExport(surveyId, audienceId, selectedContactIds)
			.then((response) => {
				FileDownloadHelper.download(response, 'DistributionList.xlsx');
			})
			.catch((ex) => {
				dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			});
	},
};

export const reducer: Reducer<IDistributionContactState, KnownActions> = (
	state: IDistributionContactState = defaultState,
	action: KnownActions,
) => {
	switch (action.type) {
		case ActionTypes.DISTRIBUTION_CONTACTS_GET_REQUESTED:
			return { ...state, loadingContacts: true };

		case ActionTypes.DISTRIBUTION_CONTACTS_GET_SUCCEEDED: {
			return {
				...state,
				columns: [...action.payload.columns],
				distributionContacts: action.payload.pagedDistributionContacts.items,
				pageRange: action.payload.pagedDistributionContacts.pageRange,
				searchText: action.payload.searchText,
				sortParams: {
					columns: state.sortParams.columns,
					sortedBy: action.payload.sortedBy,
					sortAsc: action.payload.sortAsc,
				},
				loadingContacts: false,
			};
		}
		case ActionTypes.DISTRIBUTION_CONTACTS_GET_FAILED: {
			return {
				...state,
				loadingContacts: false,
			};
		}
		case ActionTypes.DISTRIBUTION_CONTACTS_COUNT_GET_REQUESTED:
		case ActionTypes.DISTRIBUTION_CONTACTS_COUNT_GET_FAILED: {
			return {
				...state,
				contactsCount: 0,
			};
		}
		case ActionTypes.DISTRIBUTION_CONTACTS_COUNT_GET_SUCCEEDED: {
			return {
				...state,
				contactsCount: action.payload,
			};
		}
		case ActionTypes.DISTRIBUTION_CONTACTS_MODAL_OPEN:
			return { ...state, isOpen: true, columns: [...distributionContactDefaultColumns, actionsColumn] };
		case ActionTypes.DISTRIBUTION_CONTACTS_MODAL_CLOSE:
			return { ...state, isOpen: false, selectedDistributionContacts: [] };
		case ActionTypes.DISTRIBUTION_CONTACT_CLEAR_SELECTED:
			return { ...state, selectedDistributionContacts: [] };
		case ActionTypes.DISTRIBUTION_CONTACT_DELETE_REQUESTED:
			return { ...state, loadingContacts: true };
		case ActionTypes.DISTRIBUTION_CONTACT_DELETE_SUCCEEDED: {
			const contacts = _.filter(
				state.distributionContacts,
				(contact) => contact.distributionContactId !== action.payload.distributionContactId,
			);
			return { ...state, loadingContacts: false, distributionContacts: contacts };
		}
		case ActionTypes.DISTRIBUTION_CONTACT_DELETE_FAILED:
			return { ...state, loadingContacts: false };
		case ActionTypes.DISTRIBUTION_CONTACT_MULTIPLE_DELETE_REQUESTED:
			return { ...state, isDeletingDistributionContacts: true };
		case ActionTypes.DISTRIBUTION_CONTACT_MULTIPLE_DELETE_SUCCEEDED: {
			return {
				...state,
				isDeletingDistributionContacts: false,
				isDeleteDistributionContactsModalOpen: false,
				selectedDistributionContacts: [],
			};
		}
		case ActionTypes.DISTRIBUTION_CONTACT_MULTIPLE_DELETE_FAILED:
			return { ...state, isDeletingDistributionContacts: false };
		case ActionTypes.DISTRIBUTION_CONTACTS_COLUMNS_GET_REQUESTED:
			return { ...state };
		case ActionTypes.DISTRIBUTION_CONTACTS_COLUMNS_GET_FAILED:
			return { ...state };
		case ActionTypes.DISTRIBUTION_CONTACTS_COLUMNS_GET_SUCCEEDED: {
			const attributeColumns = ColumnHelper.MapAttributesToColumns(action.payload.attributes);

			const columns: IColumn[] = _.map(
				[
					...GetSurveyParticipantColumns(action.payload.distributionContactsListSourceType, action.payload.distributionType),
					...attributeColumns,
					actionsColumn,
				],
				(c: IColumn, displayOrder: number) => ({ ...c, displayOrder }),
			);
			return { ...state, columns };
		}
		case ActionTypes.DISTRIBUTION_CONTACTS_RESET_DEFAULT_COLUMNS:
			return { ...state, columns: [...distributionContactDefaultColumns, actionsColumn] };
		case ActionTypes.RESET_RESEND_REOPEN_DEFAULT_COLUMNS:
			return { ...state, columns: [...surveyParticipantDefaultColumns, actionsColumn] };
		case ActionTypes.DISTRIBUTION_CONTACT_ADD_SELECTED: {
			const selectedDistributionContacts = state.selectedDistributionContacts;

			const newDistributionContacts = _.differenceBy(
				action.payload.distributionContacts,
				selectedDistributionContacts,
				'distributionContactId',
			);

			const updatedSelectedDistributionContacts = _.concat(selectedDistributionContacts, newDistributionContacts);
			return { ...state, selectedDistributionContacts: [...updatedSelectedDistributionContacts] };
		}
		case ActionTypes.DISTRIBUTION_CONTACT_REMOVE_SELECTED: {
			const updatedSelectedDistributionContacts = state.selectedDistributionContacts;

			const contacts = _.intersectionBy(action.payload.distributionContacts, updatedSelectedDistributionContacts, 'distributionContactId');
			_.remove(updatedSelectedDistributionContacts, (contact) => {
				return (
					_.findIndex(contacts, (dc) => {
						return dc.distributionContactId === contact.distributionContactId;
					}) > -1
				);
			});
			return { ...state, selectedDistributionContacts: [...updatedSelectedDistributionContacts] };
		}
		case ActionTypes.DISTRIBUTION_CONTACT_TOGGLE_DELETE_WARNING_MODAL: {
			return { ...state, isDeleteDistributionContactsModalOpen: action.payload };
		}
		case ActionTypes.DISTRIBUTION_CONTACT_UPDATE_STATUS_AFTER_REOPEN: {
			const updatedSelectedDistributionContacts = state.selectedDistributionContacts.map((contact) => {
				contact.surveyCompletionStatusId = SurveyCompletionStatusType.InProgress;
				return contact;
			});

			return { ...state, selectedDistributionContacts: updatedSelectedDistributionContacts };
		}
		case ActionTypes.DISTRIBUTION_CONTACT_UPDATE_STATUS_AFTER_RESET: {
			const updatedSelectedDistributionContacts = state.selectedDistributionContacts.map((contact) => {
				contact.surveyCompletionStatusId = SurveyCompletionStatusType.NotStarted;
				return contact;
			});

			return { ...state, selectedDistributionContacts: updatedSelectedDistributionContacts };
		}
		case ActionTypes.DISTRIBUTION_CONTACTS_TOGGLE_DUPLICATE_EMAIL_SWITCH: {
			return { ...state, displayDuplicateEmailAddresses: !state.displayDuplicateEmailAddresses };
		}
		case ActionTypes.DISTRIBUTION_CONTACTS_SET_DISPLAY_DUPLICATE_EMAIL_STATE: {
			return { ...state, displayDuplicateEmailAddresses: action.payload };
		}
		default:
			return state;
	}
};
